import { useState } from "react";
import { Files } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputClass } from "@/components/FieldInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function BatchReportButton() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ year: new Date().getFullYear() - 1, format: "pdf", include_istituiti: false, archive: false });
  const [busy, setBusy] = useState(false);
  if (!can("accounting")) return null;
  const years = Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - i);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/batch", f, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `Rendiconti_${f.year}.zip`;
      a.click();
      URL.revokeObjectURL(href);
      toast.success(`Rendiconti generati per ${r.headers["x-trust-count"] || ""} Trust${f.archive ? " e archiviati nei documenti" : ""}`);
      setOpen(false);
    } catch (e) {
      let msg = errMsg(e);
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* risposta non JSON */ } }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="batch-report-open"><Files className="h-4 w-4 mr-1" />Rendiconti di fine anno</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="batch-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rendiconti di fine anno</DialogTitle>
            <DialogDescription>Genera in un solo clic il rendiconto di tutti i Trust attivi visibili al tuo profilo. Ricevi un archivio ZIP con l'elenco dei file e le loro impronte SHA-256.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Esercizio</label>
              <select className={inputClass} value={f.year} onChange={(e) => setF({ ...f, year: Number(e.target.value) })} data-testid="batch-report-year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Formato</label>
              <select className={inputClass} value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })} data-testid="batch-report-format">
                <option value="pdf">PDF</option><option value="docx">Word</option><option value="both">PDF + Word</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#0A2540]" checked={f.include_istituiti} onChange={(e) => setF({ ...f, include_istituiti: e.target.checked })} data-testid="batch-report-istituiti" />Includi anche i Trust in stato "Istituito"</label>
            {can("documents", "c") && (
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#0A2540]" checked={f.archive} onChange={(e) => setF({ ...f, archive: e.target.checked })} data-testid="batch-report-archive" />Archivia ogni rendiconto nei Documenti del rispettivo Trust</label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button disabled={busy} onClick={run} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="batch-report-run">{busy ? "Generazione in corso…" : "Genera rendiconti"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
